import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCustomerOrderHistory } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Trash2, Package, Tag, ArrowRight, User, MapPin, Hash, Info, Search, Users, Check, X, History, Clock, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PLACEHOLDER_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/a6ade5f6-3444-4c35-926e-b151ef73f87c/images/2ded88222df5c0bc13af15a2d9282c5dd6dd7a7aed6be2ca8410dd3d1a4a8229.png';

// 価格表示（0の場合は「時価」と表示）
const formatPrice = (price) => {
  if (price === 0 || price === null || price === undefined) {
    return '時価';
  }
  return `¥${price.toLocaleString()}`;
};

export const ProxyOrder = () => {
  // 顧客情報
  const [customerName, setCustomerName] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [routeNumber, setRouteNumber] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null); // 選択した既存顧客のID
  
  // カート
  const [cartItems, setCartItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  
  // データ
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]); // 既存顧客リスト
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // UI状態
  const [searchQuery, setSearchQuery] = useState('');
  const [customerSearchQuery, setCustomerSearchQuery] = useState(''); // 顧客検索
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false); // 新規顧客入力モード
  
  // 注文履歴
  const [orderHistory, setOrderHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [catalogRes, datesRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/api/catalog`, { headers }),
        axios.get(`${API_URL}/api/available-dates`, { headers }),
        axios.get(`${API_URL}/api/customers`, { headers }),
      ]);
      
      setProducts(catalogRes.data.products || []);
      setSales(catalogRes.data.sales || []);
      setAvailableDates(datesRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 既存顧客を選択
  const selectExistingCustomer = async (customer) => {
    setSelectedCustomerId(customer.id);
    setCustomerName(customer.name);
    setCustomerCode(customer.code || '');
    setRouteNumber(customer.route_number || '');
    setShowNewCustomerForm(false);
    toast.success(`${customer.name}を選択しました`, { duration: 1500 });
    
    // 注文履歴を取得
    fetchOrderHistory(customer.id);
  };

  // 注文履歴を取得
  const fetchOrderHistory = async (customerId) => {
    setLoadingHistory(true);
    try {
      const res = await getCustomerOrderHistory(customerId);
      setOrderHistory(res.data);
    } catch (error) {
      console.error('注文履歴の取得に失敗:', error);
      setOrderHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  // 顧客選択をクリア
  const clearCustomerSelection = () => {
    setSelectedCustomerId(null);
    setCustomerName('');
    setCustomerCode('');
    setRouteNumber('');
    setShowNewCustomerForm(false);
    setOrderHistory(null);
  };

  // 新規顧客入力モードに切り替え
  const switchToNewCustomer = () => {
    setSelectedCustomerId(null);
    setCustomerName('');
    setCustomerCode('');
    setRouteNumber('');
    setShowNewCustomerForm(true);
  };

  // 顧客フィルタ
  const filteredCustomers = customers.filter((c) => {
    if (!customerSearchQuery) return true;
    const query = customerSearchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(query) ||
      c.code?.toLowerCase().includes(query) ||
      c.route_number?.toLowerCase().includes(query)
    );
  });

  const addToCart = (item) => {
    const existingIndex = cartItems.findIndex(
      (i) => i.id === item.id && i.item_type === item.item_type
    );
    
    if (existingIndex >= 0) {
      const updated = [...cartItems];
      updated[existingIndex].quantity += 1;
      setCartItems(updated);
    } else {
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
    toast.success(`${item.name}を追加しました`, { duration: 1500 });
  };

  // 履歴から商品をカートに追加（商品名で検索してマッチ）
  const addFromHistory = (historyItem) => {
    // 現在のカタログから同名の商品を探す
    const allItems = [...sales, ...products];
    
    // 完全一致を試みる
    let matchedItem = allItems.find(
      (item) => 
        item.name === historyItem.item_name &&
        (item.origin || '') === (historyItem.origin || '') &&
        (item.spec || '') === (historyItem.spec || '')
    );
    
    // 完全一致がない場合は商品名のみでマッチング
    if (!matchedItem) {
      matchedItem = allItems.find(
        (item) => item.name === historyItem.item_name
      );
    }
    
    if (matchedItem) {
      addToCart(matchedItem);
    } else {
      // カタログにない場合はエラーメッセージを表示
      toast.error(`「${historyItem.item_name}」は現在のカタログにありません。商品一覧から選択してください。`, { duration: 3000 });
    }
  };

  // 過去の注文を丸ごとカートにコピー
  const copyOrderToCart = (order) => {
    const orderItems = order.items || [];
    const allCatalogItems = [...sales, ...products];
    const itemsToAdd = [];
    const notFoundItems = [];
    
    orderItems.forEach((orderItem) => {
      // 完全一致を試みる
      let matchedItem = allCatalogItems.find(
        (item) => 
          item.name === orderItem.item_name &&
          (item.origin || '') === (orderItem.origin || '') &&
          (item.spec || '') === (orderItem.spec || '')
      );
      
      // 完全一致がない場合は商品名のみでマッチング
      if (!matchedItem) {
        matchedItem = allCatalogItems.find(
          (item) => item.name === orderItem.item_name
        );
      }
      
      if (matchedItem) {
        itemsToAdd.push({ ...matchedItem, quantity: orderItem.quantity });
      } else {
        notFoundItems.push(orderItem.item_name);
      }
    });
    
    if (itemsToAdd.length > 0) {
      // 一括でカートに追加（既存アイテムとマージ）
      setCartItems((prevCart) => {
        const newCart = [...prevCart];
        
        itemsToAdd.forEach((newItem) => {
          const existingIndex = newCart.findIndex(
            (i) => i.id === newItem.id && i.item_type === newItem.item_type
          );
          
          if (existingIndex >= 0) {
            newCart[existingIndex] = {
              ...newCart[existingIndex],
              quantity: newCart[existingIndex].quantity + newItem.quantity
            };
          } else {
            newCart.push(newItem);
          }
        });
        
        return newCart;
      });
      
      toast.success(`${itemsToAdd.length}件の商品をカートに追加しました`, { duration: 2000 });
    }
    
    if (notFoundItems.length > 0) {
      toast.error(`${notFoundItems.length}件の商品がカタログにありません: ${notFoundItems.join(', ')}`, { duration: 4000 });
    }
    
    if (itemsToAdd.length === 0 && notFoundItems.length > 0) {
      toast.error('カタログに該当する商品が見つかりませんでした');
    }
  };

  const updateQuantity = (id, itemType, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(id, itemType);
      return;
    }
    setCartItems(cartItems.map((item) =>
      item.id === id && item.item_type === itemType
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (id, itemType) => {
    setCartItems(cartItems.filter(
      (item) => !(item.id === id && item.item_type === itemType)
    ));
  };

  const totalAmount = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleSubmit = async () => {
    if (!customerName.trim()) {
      toast.error('顧客名を入力してください');
      return;
    }
    if (cartItems.length === 0) {
      toast.error('商品を選択してください');
      return;
    }
    if (!deliveryDate) {
      toast.error('配送希望日を選択してください');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/proxy-orders`,
        {
          customer_name: customerName,
          customer_code: customerCode,
          route_number: routeNumber,
          items: cartItems.map((item) => ({
            item_id: item.id,
            item_type: item.item_type,
            quantity: item.quantity,
          })),
          notes,
          delivery_date: deliveryDate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('代理発注を完了しました');
      navigate('/orders');
    } catch (error) {
      const message = error.response?.data?.detail || '発注に失敗しました';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleShowDetail = (item) => {
    setSelectedItem(item);
    setDetailDialogOpen(true);
  };

  // 検索フィルタ
  const filterItems = (items) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.name?.toLowerCase().includes(query) ||
        item.origin?.toLowerCase().includes(query) ||
        item.spec?.toLowerCase().includes(query)
    );
  };

  const filteredSales = filterItems(sales);
  const filteredProducts = filterItems(products);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="proxy-order-page">
      <div className="page-header">
        <h1 className="page-title">代理発注</h1>
        <p className="text-sm text-slate-500">顧客に代わって発注を行います</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左側: 商品選択 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 顧客選択セクション */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                  <Users className="h-5 w-5" />
                  顧客選択
                </CardTitle>
                {(selectedCustomerId || showNewCustomerForm) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearCustomerSelection}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    選択解除
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* 選択済み顧客の表示 */}
              {selectedCustomerId && (
                <div className="p-4 bg-white border-2 border-blue-400 rounded-lg mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <Check className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-bold text-lg">{customerName}</p>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          {customerCode && <span className="font-mono">{customerCode}</span>}
                          {routeNumber && <span>ルート: {routeNumber}</span>}
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800">選択中</Badge>
                  </div>
                </div>
              )}

              {/* 新規顧客入力フォーム */}
              {showNewCustomerForm && (
                <div className="p-4 bg-white border-2 border-green-400 rounded-lg mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className="bg-green-100 text-green-800">新規顧客</Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <User className="h-3 w-3" /> 顧客名 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="例: 山田商店"
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <Hash className="h-3 w-3" /> 顧客コード
                      </Label>
                      <Input
                        value={customerCode}
                        onChange={(e) => setCustomerCode(e.target.value)}
                        placeholder="例: CUST-001"
                        data-testid="customer-code-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> ルート番号
                      </Label>
                      <Input
                        value={routeNumber}
                        onChange={(e) => setRouteNumber(e.target.value)}
                        placeholder="例: R-01"
                        data-testid="route-number-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 顧客選択UI（未選択時） */}
              {!selectedCustomerId && !showNewCustomerForm && (
                <>
                  {/* 新規顧客ボタン */}
                  <Button
                    variant="outline"
                    className="w-full mb-4 border-green-300 text-green-700 hover:bg-green-50"
                    onClick={switchToNewCustomer}
                    data-testid="new-customer-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    新規顧客として入力
                  </Button>

                  {/* 既存顧客検索 */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="顧客名、コード、ルートで検索..."
                      value={customerSearchQuery}
                      onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="customer-search-input"
                    />
                  </div>

                  {/* 既存顧客リスト（チケット/カード形式） */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {filteredCustomers.map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => selectExistingCustomer(customer)}
                        className="p-3 text-left border-2 border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors group"
                        data-testid={`customer-select-${customer.id}`}
                      >
                        <p className="font-medium text-sm truncate group-hover:text-blue-700">
                          {customer.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {customer.code && (
                            <span className="text-xs font-mono text-slate-500 bg-slate-100 px-1 rounded">
                              {customer.code}
                            </span>
                          )}
                          {customer.route_number && (
                            <span className="text-xs text-slate-400">
                              R:{customer.route_number}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {filteredCustomers.length === 0 && (
                    <p className="text-center text-slate-400 py-4">該当する顧客がありません</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* 注文履歴セクション（顧客選択時に表示） */}
          {selectedCustomerId && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2 text-purple-700">
                    <History className="h-5 w-5" />
                    注文履歴から選択
                    {orderHistory && (
                      <Badge className="bg-purple-100 text-purple-800">
                        過去{orderHistory.total_orders}件
                      </Badge>
                    )}
                  </CardTitle>
                  {selectedCustomerId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchOrderHistory(selectedCustomerId)}
                      disabled={loadingHistory}
                      className="text-purple-600"
                    >
                      <RefreshCw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                    <span className="ml-2 text-sm text-purple-600">履歴を読み込み中...</span>
                  </div>
                ) : orderHistory ? (
                  <div className="space-y-4">
                    {/* よく注文する商品 */}
                    {orderHistory.frequent_items?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-1">
                          <Tag className="h-3 w-3" />
                          よく注文する商品
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {orderHistory.frequent_items.slice(0, 10).map((item, idx) => (
                            <button
                              key={idx}
                              onClick={() => addFromHistory(item)}
                              className="px-3 py-2 text-sm bg-white border border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center gap-2"
                              data-testid={`history-item-${idx}`}
                            >
                              <span className="font-medium">{item.item_name}</span>
                              {item.origin && <span className="text-xs text-slate-500">{item.origin}</span>}
                              <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                                {item.count}回
                              </span>
                              <Plus className="h-3 w-3 text-purple-600" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 直近の注文 */}
                    {orderHistory.recent_orders?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-purple-800 mb-2 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          直近の注文
                        </h4>
                        <div className="space-y-2">
                          {orderHistory.recent_orders.map((order, idx) => (
                            <div
                              key={order.id || idx}
                              className="p-3 bg-white border border-purple-100 rounded-lg"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">
                                    {order.delivery_date && format(new Date(order.delivery_date), 'MM/dd(E)', { locale: ja })}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {order.items?.length || 0}点
                                  </Badge>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                                  onClick={() => copyOrderToCart(order)}
                                  data-testid={`copy-order-${idx}`}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  この注文をコピー
                                </Button>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {order.items?.slice(0, 5).map((item, itemIdx) => (
                                  <span
                                    key={itemIdx}
                                    className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded"
                                  >
                                    {item.item_name} ×{item.quantity}
                                  </span>
                                ))}
                                {order.items?.length > 5 && (
                                  <span className="text-xs text-slate-400">
                                    他{order.items.length - 5}点
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 履歴がない場合 */}
                    {(!orderHistory.frequent_items || orderHistory.frequent_items.length === 0) &&
                     (!orderHistory.recent_orders || orderHistory.recent_orders.length === 0) && (
                      <p className="text-center text-slate-400 py-4">
                        この顧客の注文履歴はまだありません
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-slate-400 py-4">
                    履歴を読み込めませんでした
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* 検索 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="商品名、産地、規格で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>

          {/* 特売商品 - リスト表示 */}
          {filteredSales.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                  <Tag className="h-5 w-5" />
                  特売商品
                  <Badge className="bg-red-100 text-red-800">{filteredSales.length}点</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {filteredSales.map((item) => (
                    <ProductListItem
                      key={item.id}
                      item={item}
                      isSale={true}
                      onAdd={() => addToCart(item)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* レギュラー商品 - リスト表示 */}
          {filteredProducts.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  レギュラー商品
                  <Badge className="bg-slate-100 text-slate-800">{filteredProducts.length}点</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {filteredProducts.map((item) => (
                    <ProductListItem
                      key={item.id}
                      item={item}
                      isSale={false}
                      onAdd={() => addToCart(item)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* 右側: カート */}
        <div>
          <Card className="border-slate-200 sticky top-24">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                発注内容
                {cartItems.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-800">{cartItems.length}点</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cartItems.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">商品を選択してください</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div
                      key={`${item.id}-${item.item_type}`}
                      className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.name}</p>
                        <p className="text-xs text-slate-500">
                          {formatPrice(item.price)}{item.price !== 0 && `/${item.unit}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateQuantity(item.id, item.item_type, item.quantity - 1)}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateQuantity(item.id, item.item_type, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-red-500"
                          onClick={() => removeFromCart(item.id, item.item_type)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3 pt-3 border-t">
                <div className="space-y-2">
                  <Label>配送希望日 <span className="text-red-500">*</span></Label>
                  <Select value={deliveryDate} onValueChange={setDeliveryDate}>
                    <SelectTrigger data-testid="delivery-date-select">
                      <SelectValue placeholder="日付を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDates.map((d) => (
                        <SelectItem key={d.date} value={d.date}>
                          {format(new Date(d.date), 'MM月dd日(E)', { locale: ja })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>申し送り事項</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="備考やメモ"
                    rows={2}
                    data-testid="notes-input"
                  />
                </div>

                <div className="flex justify-between text-lg font-bold pt-3 border-t">
                  <span>合計金額</span>
                  <span className="font-mono text-blue-600">¥{totalAmount.toLocaleString()}</span>
                </div>

                <Button
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleSubmit}
                  disabled={submitting || cartItems.length === 0 || !customerName || !deliveryDate}
                  data-testid="submit-proxy-order-btn"
                >
                  {submitting ? '発注中...' : '代理発注を確定'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 商品詳細ダイアログ */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem?.item_type === 'sale' && (
                <Badge className="bg-red-500 text-white">特売</Badge>
              )}
              {selectedItem?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedItem?.image_url && (
              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                <img
                  src={selectedItem.image_url}
                  alt={selectedItem.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${selectedItem?.item_type === 'sale' ? 'text-red-600' : 'text-slate-900'}`}>
                ¥{selectedItem?.price?.toLocaleString()}
              </span>
              <span className="text-slate-500">/{selectedItem?.unit}</span>
            </div>
            {(selectedItem?.origin || selectedItem?.spec) && (
              <p className="text-sm text-slate-500">
                {selectedItem?.origin && <span>産地: {selectedItem.origin}</span>}
                {selectedItem?.origin && selectedItem?.spec && <span> / </span>}
                {selectedItem?.spec && <span>規格: {selectedItem.spec}</span>}
              </p>
            )}
            {selectedItem?.description && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {selectedItem.description}
                </p>
              </div>
            )}
            <Button
              className={`w-full ${selectedItem?.item_type === 'sale' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              onClick={() => {
                addToCart(selectedItem);
                setDetailDialogOpen(false);
              }}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              カートに追加
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// 商品カードコンポーネント
const ProductCard = ({ item, isSale, onAdd, onDetail }) => (
  <div className={`p-2 border rounded-lg ${isSale ? 'border-red-200 bg-red-50/30' : 'border-slate-200'}`}>
    <div className="aspect-square bg-slate-100 rounded mb-2 overflow-hidden relative">
      {item.image_url ? (
        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="relative w-full h-full">
          <img src={PLACEHOLDER_IMAGE} alt="画像準備中" className="w-full h-full object-cover opacity-70" loading="lazy" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-white/80 text-slate-600 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
              画像準備中
            </span>
          </div>
        </div>
      )}
      {isSale && (
        <Badge className="absolute top-1 left-1 bg-red-500 text-white text-[10px] px-1">特売</Badge>
      )}
    </div>
    <h4 className="text-xs font-medium truncate">{item.name}</h4>
    <p className="text-[10px] text-slate-500 truncate">
      {item.origin || '-'} / {item.spec || '-'}
    </p>
    <p className={`text-sm font-bold ${isSale ? 'text-red-600' : 'text-slate-900'}`}>
      ¥{item.price?.toLocaleString()}/{item.unit}
    </p>
    <div className="flex gap-1 mt-1">
      {item.description && (
        <Button
          variant="ghost"
          size="sm"
          className="flex-1 h-7 text-[10px] text-slate-500"
          onClick={onDetail}
        >
          <Info className="h-3 w-3 mr-0.5" />
          詳細
        </Button>
      )}
      <Button
        size="sm"
        className={`flex-1 h-7 text-[10px] ${isSale ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
        onClick={onAdd}
      >
        <Plus className="h-3 w-3 mr-0.5" />
        追加
      </Button>
    </div>
  </div>
);

// 商品リストアイテムコンポーネント（写真なし・コンパクト）
const ProductListItem = ({ item, isSale, onAdd }) => (
  <div 
    className={`flex items-center justify-between p-2 border rounded-lg hover:bg-slate-50 transition-colors ${
      isSale ? 'border-red-200 bg-red-50/30 hover:bg-red-50' : 'border-slate-200'
    }`}
  >
    <div className="flex items-center gap-3 flex-1 min-w-0">
      {isSale && (
        <Badge className="bg-red-500 text-white text-[10px] px-1.5 shrink-0">特売</Badge>
      )}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium truncate">{item.name}</h4>
        <p className="text-xs text-slate-500 truncate">
          {item.origin || '-'} / {item.spec || '-'}
        </p>
      </div>
      <p className={`text-sm font-bold shrink-0 ${isSale ? 'text-red-600' : 'text-slate-900'}`}>
        {formatPrice(item.price)}{item.price !== 0 && `/${item.unit}`}
      </p>
    </div>
    <Button
      size="sm"
      className={`ml-2 h-8 px-3 ${isSale ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
      onClick={onAdd}
    >
      <Plus className="h-4 w-4" />
    </Button>
  </div>
);
